import { TAGHIGHLIGHT_BACKDROP_HEIGHT } from "components/Dashboard/HubletView/SingleTimeline/TagHighlightTimelineBackdrop";
import React, { useRef, useEffect, memo } from "react";
import { useTimeAxisContext } from "utils/customHooks/genericHooks";
import {
  loadingSubtimelineBackgroundColor,
  skuSegmentColor,
  subTimelineLoadingDashColor,
} from "./skuOperatorTimelineUtils";
import { safariOffset } from "./safariOffset";
import { useProcessIdOnSingleProcessPage } from "components/CustomGridItems/LotInfoBar/lotInfoUtils";
import {
  useIsTimelineExpanded,
  useTeamSizeTimelineSegments,
} from "components/Dashboard/HubletView/SingleTimeline/singleTimelineStore";

// /** grey bar across the full width, same size as the segment */
// function GreySubTimelineBackdrop(
//   props: React.SVGProps<SVGRectElement> & { y: number }
// ) {
//   return (
//     <rect
//       className="grey-sub-timeline-backdrop"
//       {...props}
//       fill="#f0f0f0"
//       height={TAGHIGHLIGHT_BACKDROP_HEIGHT}
//       width={"100%"}
//       y={props.y}
//     />
//   );
// }
interface TeamSizeCanvasProps {
  width: number;
  isTimelineExpanded: boolean;
  teamSizeSegments: {
    teamSize: number | null;
    range: { ge: number; lt?: number };
  }[];
  processId: string;
}
/**
 * This tooltip tracks which team size segment the mouse is over
 * and displays a tooltip with the relevant information
 */
export const TeamSizeTooltip = ({ order }: { order: number }) => {
  const [isTimelineExpanded] = useIsTimelineExpanded();
  return isTimelineExpanded ? <TeamSizeTooltipContent /> : null;
};

/**
 * Component that creates and manages tooltips for team size segments
 */
const TeamSizeTooltipContent = memo(() => {
  // Get context and data needed for tooltip display
  const { scaleTime } = useTimeAxisContext();
  const teamSizeSegments = useTeamSizeTimelineSegments();
  const processId = useProcessIdOnSingleProcessPage();

  // Create a tooltip element in the DOM on component mount
  useEffect(() => {
    // Create tooltip element if it doesn't exist
    let tooltip = document.getElementById("team-size-tooltip");
    if (!tooltip) {
      tooltip = document.createElement("div");
      tooltip.id = "team-size-tooltip";
      tooltip.style.position = "fixed";
      tooltip.style.display = "none";
      tooltip.style.backgroundColor = "#fff";
      tooltip.style.color = "#333";
      tooltip.style.padding = "8px 12px";
      tooltip.style.borderRadius = "4px";
      tooltip.style.fontSize = "12px";
      tooltip.style.zIndex = "10000";
      tooltip.style.pointerEvents = "none";
      tooltip.style.boxShadow = "0 2px 10px rgba(0,0,0,0.2)";

      // Add tooltip arrow
      const arrow = document.createElement("div");
      arrow.id = "team-size-tooltip-arrow";
      arrow.style.position = "absolute";
      arrow.style.top = "-8px";
      arrow.style.left = "50%";
      arrow.style.marginLeft = "-8px";
      arrow.style.borderLeft = "8px solid transparent";
      arrow.style.borderRight = "8px solid transparent";
      arrow.style.borderBottom = "8px solid #fff";
      tooltip.appendChild(arrow);

      // Add tooltip content container
      const content = document.createElement("div");
      content.id = "team-size-tooltip-content";
      tooltip.appendChild(content);

      document.body.appendChild(tooltip);
    }

    // Store references to event handlers and timers for cleanup
    let canvasTimerRef: number | null = null;
    const eventListenerMap = new Map<
      string,
      {
        element: HTMLElement;
        listeners: { type: string; handler: EventListener }[];
      }
    >();

    // Add event handlers to canvas elements
    const addCanvasHandlers = () => {
      if (!processId) {
        console.log("No process ID available");
        return;
      }

      // Find canvas elements in the DOM
      const teamSizeCanvas = document.getElementById(
        `team-size-canvas-${processId}`
      ) as HTMLElement | null;

      if (!teamSizeCanvas) {
        // Try again in 100ms if canvases aren't found
        console.log("Canvas elements not found, retrying...");
        canvasTimerRef = window.setTimeout(addCanvasHandlers, 100);
        return;
      }

      // Handler for mouse movement over canvas
      const handleMouseMove = (e: MouseEvent) => {
        if (!tooltip) {
          return;
        }

        const canvas = e.currentTarget as HTMLCanvasElement;
        const rect = canvas.getBoundingClientRect();
        const x = e.clientX - rect.left;

        // Get segments for this process
        const segments =
          processId && teamSizeSegments[processId]
            ? teamSizeSegments[processId]
            : [];

        let found = false;

        for (const segment of segments) {
          if (!segment.range?.ge) {
            continue;
          }

          const x0 = scaleTime(segment.range.ge) || 0;
          const x1 = segment.range.lt
            ? scaleTime(segment.range.lt) || 0
            : scaleTime(Date.now());

          // Segment is visible if it overlaps the visible canvas (partially or fully)
          // Check if segment overlaps visible area (x0 < canvas.width && x1 > 0)
          if (x0 < (canvas.width || 0) && x1 > 0) {
            // Only show tooltip if mouse is within segment bounds
            if (x >= x0 && x <= x1) {
              found = true;

              // Format time strings
              const startTime = new Date(segment.range.ge).toLocaleTimeString();
              // Fix: Use the actual end time for tooltip display
              let endTime;
              if (segment.range.lt) {
                // If segment has a defined end time, use it
                endTime = new Date(segment.range.lt).toLocaleTimeString();
              } else {
                // Use the calculated end time instead of just showing "Now"
                // Calculate the time using the inverse of scaleTime (unscale the x coordinate)
                const unscaledTime = scaleTime.invert(x1);
                endTime = unscaledTime
                  ? new Date(unscaledTime).toLocaleTimeString()
                  : new Date().toLocaleTimeString();
              }

              // Set tooltip content
              const content = document.getElementById(
                "team-size-tooltip-content"
              );
              if (!content) {
                return;
              }

              content.innerHTML = `
                <div class="title" style="font-weight:bold;margin-bottom:6px;font-size:13px;">
                  Team Size
                </div>
                <div style="margin-bottom:4px">
                  <strong>Value:</strong> ${segment.teamSize?.toFixed(0) ?? "--"}
                </div>
                <div>
                  <strong>Time:</strong> ${startTime} - ${endTime}
                </div>
              `;

              // Position tooltip below cursor with the arrow centered on the mouse
              const tooltipWidth = 208; // Approximate width of tooltip

              // Calculate ideal left position (centered on mouse)
              let leftPosition = e.clientX - tooltipWidth / 2;

              // Adjust for screen boundaries
              leftPosition = Math.max(
                10,
                Math.min(leftPosition, window.innerWidth - tooltipWidth - 10)
              );

              // Update tooltip position
              tooltip.style.left = `${leftPosition}px`;
              tooltip.style.top = `${rect.bottom + 5 + -(2 * safariOffset)}px`;
              tooltip.style.display = "block";
              tooltip.style.opacity = "1"; // Reset opacity when data is found

              // Adjust arrow position to align with mouse
              const arrow = document.getElementById("team-size-tooltip-arrow");
              if (arrow) {
                // Position arrow relative to mouse and tooltip
                // Calculate exact pixel position of mouse relative to tooltip
                const arrowLeftPixels = e.clientX - leftPosition;

                // Set absolute positioning instead of percentage to avoid rounding errors
                arrow.style.left = "";
                arrow.style.right = "";
                arrow.style.marginLeft = "0";

                // Position arrow directly below mouse
                if (
                  arrowLeftPixels >= 8 &&
                  arrowLeftPixels <= tooltipWidth - 8
                ) {
                  // Safe to position directly under mouse
                  arrow.style.left = `${arrowLeftPixels}px`;
                  arrow.style.marginLeft = "-8px";
                } else if (arrowLeftPixels < 8) {
                  // Too close to left edge, use minimum position
                  arrow.style.left = "8px";
                  arrow.style.marginLeft = "-8px";
                } else {
                  // Too close to right edge, use maximum position
                  arrow.style.right = "8px";
                  arrow.style.marginLeft = "-8px";
                }
              }
              break;
            }
          }
        }

        if (!found) {
          // Show tooltip with empty values when no segment is found
          const content = document.getElementById("team-size-tooltip-content");
          if (!content) {
            return;
          }

          content.innerHTML = `
            <div class="title" style="font-weight:bold;margin-bottom:6px;font-size:13px;">
              Team Size
            </div>
            <div style="margin-bottom:4px">
              <strong>Value:</strong> --
            </div>
            <div>
              <strong>Time:</strong> --
            </div>
          `;

          // Position tooltip below cursor with the arrow centered on the mouse
          const tooltipWidth = 208; // Fixed width for tooltip
          tooltip.style.width = `${tooltipWidth - 24}px`; // Subtract padding (12px on each side)

          // Calculate ideal left position (centered on mouse)
          let leftPosition = e.clientX - tooltipWidth / 2;

          // Adjust for screen boundaries
          leftPosition = Math.max(
            10,
            Math.min(leftPosition, window.innerWidth - tooltipWidth - 10)
          );

          // Update tooltip position
          tooltip.style.left = `${leftPosition}px`;
          tooltip.style.top = `${rect.bottom + 5 + -(2 * safariOffset)}px`;
          tooltip.style.display = "block";
          tooltip.style.opacity = "0.6";

          // Hide arrow when no segment is found
          const arrow = document.getElementById("team-size-tooltip-arrow");
          if (arrow) {
            arrow.style.display = "none";
          }
        }
      };

      // Handler for mouse leaving canvas
      const handleMouseLeave = () => {
        if (tooltip) {
          tooltip.style.display = "none";
        }
      };

      // Add event listeners with proper type handling
      if (teamSizeCanvas) {
        // Store handlers for cleanup
        const handlers = [
          { type: "mousemove", handler: handleMouseMove as EventListener },
          { type: "mouseleave", handler: handleMouseLeave as EventListener },
        ];

        // Add listeners
        handlers.forEach(({ type, handler }) => {
          teamSizeCanvas.addEventListener(type, handler);
        });

        // Save for cleanup
        eventListenerMap.set(teamSizeCanvas.id, {
          element: teamSizeCanvas,
          listeners: handlers,
        });
      }
    };

    // Start checking for canvases
    addCanvasHandlers();

    // Cleanup function
    return () => {
      // Remove tooltip if it exists
      if (tooltip && tooltip.parentNode) {
        tooltip.parentNode.removeChild(tooltip);
      }

      // Clear any pending timers
      if (canvasTimerRef !== null) {
        window.clearTimeout(canvasTimerRef);
      }

      // Remove all event listeners
      eventListenerMap.forEach(({ element, listeners }) => {
        listeners.forEach(({ type, handler }) => {
          element.removeEventListener(type, handler);
        });
      });
    };
  }, [processId, teamSizeSegments, scaleTime]);

  return null;
});

// Helper function to draw loading indicator pattern
const drawLoadingIndicator = (
  ctx: CanvasRenderingContext2D,
  x: number,
  width: number,
  height: number
) => {
  const middleY = height / 2;
  const dashColor = subTimelineLoadingDashColor;

  // Save current context state
  ctx.save();

  // Draw a dashed line across the middle of the area
  ctx.strokeStyle = dashColor;
  ctx.lineWidth = 2;
  ctx.setLineDash([8, 4]); // Dashed line pattern

  // Draw the horizontal dashed line
  ctx.beginPath();
  ctx.moveTo(x, middleY);
  ctx.lineTo(x + width, middleY);
  ctx.stroke();

  // Reset line dash and restore context
  ctx.setLineDash([]);
  ctx.restore();
};

export const TeamSizeCanvas: React.FC<TeamSizeCanvasProps> = ({
  width,
  isTimelineExpanded,
  teamSizeSegments,
  processId,
}) => {
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const { scaleTime } = useTimeAxisContext();

  // Draw segments on canvas
  useEffect(() => {
    if (!canvasRef.current) {
      return;
    }
    const canvas = canvasRef.current;
    const ctx = canvas?.getContext?.("2d");
    if (!ctx) {
      return;
    }
    if (isTimelineExpanded) {
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      // draw backdrop grey rectangle
      ctx.fillStyle = loadingSubtimelineBackgroundColor;
      ctx.fillRect(0, 0, canvas.width, canvas.height);

      // Get current time position on the canvas
      const nowX = scaleTime(Date.now());

      // If there are no segments or the segments array is empty, show the entire area as loading
      // but only up to the current time
      if (!teamSizeSegments || teamSizeSegments.length === 0) {
        // Create a loading indicator for the entire visible area up to now
        const loadingWidth = Math.min(nowX, canvas.width);
        drawLoadingIndicator(
          ctx,
          0,
          loadingWidth,
          TAGHIGHLIGHT_BACKDROP_HEIGHT
        );
      } else {
        // Sort segments by start time to find gaps
        const sortedSegments = [...teamSizeSegments].sort((a, b) => {
          return (a.range?.ge || 0) - (b.range?.ge || 0);
        });

        // Find gaps between segments and mark them as loading areas
        for (let i = 0; i < sortedSegments.length - 1; i++) {
          const currentSegment = sortedSegments[i];
          const nextSegment = sortedSegments[i + 1];

          // Skip if current segment doesn't have an end time
          if (!currentSegment.range?.lt) {
            continue;
          }

          // Check if there's a gap between this segment and the next
          if (currentSegment.range.lt < nextSegment.range.ge) {
            // There's a gap - draw loading indicator in the gap
            const gapStartX = scaleTime(currentSegment.range.lt) || 0;
            // Ensure we don't draw loading indicators beyond current time
            const gapEndX = Math.min(
              scaleTime(nextSegment.range.ge) || 0,
              nowX
            );
            const gapWidth = Math.max(2, gapEndX - gapStartX);

            // Only draw if gap is visible on canvas
            if (gapStartX < canvas.width && gapEndX > 0 && gapWidth > 0) {
              // Fill gap with light gray background
              ctx.fillStyle = loadingSubtimelineBackgroundColor;
              ctx.fillRect(
                gapStartX,
                0,
                gapWidth,
                TAGHIGHLIGHT_BACKDROP_HEIGHT
              );

              // Add loading indicator for the gap
              drawLoadingIndicator(
                ctx,
                gapStartX,
                gapWidth,
                TAGHIGHLIGHT_BACKDROP_HEIGHT
              );
            }
          }
        }

        // Check if there's a gap at the beginning (before first segment)
        if (sortedSegments.length > 0 && sortedSegments[0].range?.ge > 0) {
          const firstSegmentStart = scaleTime(sortedSegments[0].range.ge) || 0;
          if (firstSegmentStart > 0) {
            // Draw loading indicator from 0 to first segment
            ctx.fillStyle = loadingSubtimelineBackgroundColor;
            ctx.fillRect(0, 0, firstSegmentStart, TAGHIGHLIGHT_BACKDROP_HEIGHT);
            drawLoadingIndicator(
              ctx,
              0,
              firstSegmentStart,
              TAGHIGHLIGHT_BACKDROP_HEIGHT
            );
          }
        }

        // Check if there's a gap at the end (after last segment)
        const lastSegment = sortedSegments[sortedSegments.length - 1];
        if (lastSegment && lastSegment.range?.lt) {
          const lastSegmentEnd =
            scaleTime(lastSegment.range.lt) || canvas.width;
          // Only draw loading indicator if the last segment ends before current time
          // and before the end of the canvas
          if (lastSegmentEnd < nowX && lastSegmentEnd < canvas.width) {
            // Draw loading indicator from last segment to current time or canvas width
            const endX = Math.min(nowX, canvas.width);
            const endGapWidth = endX - lastSegmentEnd;
            ctx.fillStyle = loadingSubtimelineBackgroundColor;
            ctx.fillRect(
              lastSegmentEnd,
              0,
              endGapWidth,
              TAGHIGHLIGHT_BACKDROP_HEIGHT
            );
            drawLoadingIndicator(
              ctx,
              lastSegmentEnd,
              endGapWidth,
              TAGHIGHLIGHT_BACKDROP_HEIGHT
            );
          }
        }
      }

      // Draw each team size segment
      teamSizeSegments.forEach(segment => {
        if (!segment.range?.ge) {
          return;
        }

        const x0 = segment.range.ge ? scaleTime(segment.range.ge) || 0 : 0;
        const x1 = segment.range.lt
          ? scaleTime(segment.range.lt) || 0
          : scaleTime(Date.now());

        // Segment is visible if it overlaps the visible canvas (partially or fully)
        // Check if segment overlaps visible area (x0 < canvas.width && x1 > 0)
        if (x0 < canvas.width && x1 > 0) {
          const segmentWidth = Math.max(2, x1 - x0);

          // Determine if this segment is a loading area (missing team size data)
          const isLoadingArea = segment.teamSize === null;

          // Draw segment rectangle
          ctx.fillStyle = !segment.teamSize
            ? loadingSubtimelineBackgroundColor
            : skuSegmentColor;
          ctx.fillRect(x0, 0, segmentWidth, TAGHIGHLIGHT_BACKDROP_HEIGHT);

          // Add visual indicators for loading areas
          if (isLoadingArea) {
            drawLoadingIndicator(
              ctx,
              x0,
              segmentWidth,
              TAGHIGHLIGHT_BACKDROP_HEIGHT
            );
          }

          // Determine the visible part of the segment
          const visibleLeft = Math.max(0, x0);
          const visibleRight = Math.min(canvas.width, x1);
          const visibleWidth = visibleRight - visibleLeft;

          // Only draw text if segment is wide enough or if visible on screen
          const teamSizeText = `${segment.teamSize?.toFixed(0) ?? "--"}`;
          const textWidth = 9 + 5 * ("Team Size: " + teamSizeText).length;
          const enoughRoomForText = visibleWidth > textWidth;
          const text = enoughRoomForText
            ? `Team Size: ${teamSizeText}`
            : teamSizeText;

          // Always show text if ANY part of the segment is visible
          if (visibleWidth > 0) {
            // Draw text on top of the segment
            ctx.fillStyle = "#fff";
            ctx.font = "12px Arial";
            ctx.textAlign = "center";
            ctx.textBaseline = "middle";

            // Calculate center of visible portion for text positioning
            const visibleCenter = visibleLeft + visibleWidth / 2;

            // Center text in the visible portion of the segment
            ctx.fillText(
              text,
              visibleCenter,
              TAGHIGHLIGHT_BACKDROP_HEIGHT / 2 + 1
            );
          }
        }
      });
    } else {
      ctx.clearRect(0, 0, canvas.width, canvas.height);
    }
  }, [isTimelineExpanded, teamSizeSegments, scaleTime]);

  return (
    <div
      style={{
        position: "relative",
        width: `${width}px`,
        height: `${TAGHIGHLIGHT_BACKDROP_HEIGHT}px`,
      }}
    >
      <canvas
        ref={canvasRef}
        id={`team-size-canvas-${processId}`}
        className={"team-size-canvas"}
        width={`${width}px`}
        height={`${TAGHIGHLIGHT_BACKDROP_HEIGHT}px`}
        style={{
          width: `${width}px`,
          height: `${TAGHIGHLIGHT_BACKDROP_HEIGHT}px`,
          cursor: "pointer",
          opacity: isTimelineExpanded ? 1 : 0,
          transition: `opacity 0.5s ease-in-out 1s`,
          pointerEvents: isTimelineExpanded ? "auto" : "none",
          position: "relative",
          zIndex: 1,
        }}
      />
    </div>
  );
};
